import {
  averageValues,
} from "../core/math.js";
import { localDateFromEiPreface, localTimestampsFromClockRows } from "./local-clock.js";
import { createSourceCapture, csvSourceDocument } from "./source-data.js";
import { createParserWarning } from "../core/schema.js";
import {
  cToF,
  parseLatitude,
  parseLongitude,
  rowsFromHeader,
  valueFrom,
} from "./utils.js";
import {
  collapseGpsRefreshBursts,
  gpsPositionSparsityWarning,
  suppressHeldGpsPositions,
  suppressImplausibleGpsJumps,
} from "./data-prep.js";

const EI_CGR_30P_CYLINDER_COUNT = 6;
const CGR_RPM_SYNC_STARTUP_THRESHOLD = 500;
const CGR_RPM_SYNC_ZERO_THRESHOLD = 100;
const CGR_RPM_SYNC_MIN_SUSTAINED_SAMPLES = 3;
const CGR_RPM_SYNC_MAX_OFFSET_SECONDS = 300;
const CGR_RPM_SYNC_EDGE_SEARCH_SECONDS = 15;
const CGR_RPM_SYNC_MAX_SAMPLE_GAP_SECONDS = 2.5;
const CGR_RPM_SYNC_OFFSET_GROUP_TOLERANCE_SECONDS = 2;
// Cross-correlation fallback (no usable RPM edges in the overlap — e.g. the 30C
// powered on with the engine already running and lost power before it stopped):
// sweep clock offsets and accept the Pearson-correlation peak only when it is
// decisive — enough overlapping samples, real RPM variance on both sides, a
// high peak, and no competitive peak far from it (flat or multi-modal
// correlation curves stay on the existing fallback instead of guessing).
const CGR_RPM_CORRELATION_MIN_OVERLAP_SAMPLES = 120;
const CGR_RPM_CORRELATION_MIN_R = 0.97;
const CGR_RPM_CORRELATION_PEAK_MARGIN = 0.02;
const CGR_RPM_CORRELATION_PEAK_NEIGHBORHOOD_SECONDS = 15;
const CGR_RPM_CORRELATION_MIN_RPM_STDDEV = 10;

function requireEiCgrParserHook(hooks, name) {
  const hook = hooks?.[name];
  if (typeof hook !== "function") {
    throw new Error(`EI CGR parser requires ${name}`);
  }
  return hook;
}

function quantile(values, q) {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const index = Math.min(sorted.length - 1, Math.max(0, Math.floor((sorted.length - 1) * q)));
  return sorted[index];
}

export function eiCgrSampleRateFromModule(rows) {
  const validRows = validEiCgrClockRows(rows);
  const samplesBySecond = new Map();
  validRows.forEach((row) => {
    const displayTime = String(row.TIME ?? "").trim();
    samplesBySecond.set(displayTime, (samplesBySecond.get(displayTime) ?? 0) + 1);
  });
  return Math.max(1, Math.round(quantile([...samplesBySecond.values()], 0.8) ?? 1));
}

function timeOfDayToSeconds(value) {
  const match = String(value ?? "").trim().match(/^(\d{1,2}):(\d{2}):(\d{2})(?:\.\d+)?$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  if (hours >= 24 || minutes >= 60 || seconds >= 60) return null;
  return hours * 3600 + minutes * 60 + seconds;
}

export function validEiCgrClockRows(rows) {
  return rows.filter((row) => /^\d{2}:\d{2}:\d{2}$/.test(String(row.TIME ?? "").trim())
    && Number.isFinite(timeOfDayToSeconds(row.TIME)));
}

export function eiCgrElapsedSecondsFromModule(rows, sampleRate) {
  const rowsBySecond = new Map();
  let previousAbsolute = null;
  let dayOffset = 0;

  const absoluteSeconds = rows.map((row) => {
    const clockSeconds = timeOfDayToSeconds(row.TIME);
    if (!Number.isFinite(clockSeconds)) return null;
    if (Number.isFinite(previousAbsolute) && clockSeconds + dayOffset < previousAbsolute - 43200) {
      dayOffset += 86400;
    }
    const absoluteSecond = clockSeconds + dayOffset;
    rowsBySecond.set(absoluteSecond, (rowsBySecond.get(absoluteSecond) ?? 0) + 1);
    previousAbsolute = absoluteSecond;
    return absoluteSecond;
  });
  const occurrencesBySecond = new Map();
  const estimatedRate = Number.isFinite(sampleRate) && sampleRate > 0 ? sampleRate : 1;
  return absoluteSeconds.map((absoluteSecond) => {
    if (!Number.isFinite(absoluteSecond)) return null;
    const occurrence = occurrencesBySecond.get(absoluteSecond) ?? 0;
    occurrencesBySecond.set(absoluteSecond, occurrence + 1);
    // A short or irregular recording can underestimate the overall rate.
    // Keep every sample inside its recorded second before one-Hz resampling.
    return absoluteSecond + occurrence / Math.max(estimatedRate, rowsBySecond.get(absoluteSecond));
  });
}

export function eiCgrLocalDateFromModule(prefaceLines) {
  return localDateFromEiPreface(prefaceLines) ?? "";
}

export function parseEiCgr30pPointsFromModule(text) {
  const { rows, prefaceLines } = rowsFromHeader(text, (_line, values) => values[0] === "TIME" && values[1] === "SEL TANK QTY", 1);
  const validRows = validEiCgrClockRows(rows);
  const sampleRate = eiCgrSampleRateFromModule(validRows);
  const elapsed = eiCgrElapsedSecondsFromModule(validRows, sampleRate);
  const localDate = eiCgrLocalDateFromModule(prefaceLines);
  const timestamps = localTimestampsFromClockRows(validRows, "TIME", localDate);
  const points = validRows.map((row, index) => {
    const displayTime = String(row.TIME ?? "").trim();
    const egtValues = Array.from({ length: EI_CGR_30P_CYLINDER_COUNT }, (_, cylinder) => valueFrom(row, [`EGT${cylinder + 1};*F`]));
    const chtValues = Array.from({ length: EI_CGR_30P_CYLINDER_COUNT }, (_, cylinder) => valueFrom(row, [`CHT${cylinder + 1};*F`]));
    const rpm = averageValues([
      valueFrom(row, ["RPMLEFT;RPM"]),
      valueFrom(row, ["RPMRIGHT;RPM"]),
      valueFrom(row, ["RPM;***"]),
    ]);
    const gpsAltitude = valueFrom(row, ["GPS Altitude"]);
    const tit1 = valueFrom(row, ["TIT1;*F", "TIT 1;*F", "TIT;*F"]) ?? cToF(valueFrom(row, ["TIT1;*C", "TIT 1;*C", "TIT;*C"]));
    const tit2 = valueFrom(row, ["TIT2;*F", "TIT 2;*F"]) ?? cToF(valueFrom(row, ["TIT2;*C", "TIT 2;*C"]));
    return {
      elapsedSeconds: elapsed[index] ?? index / sampleRate,
      timestamp: timestamps[index],
      displayTime,
      phase: null,
      percentPower: valueFrom(row, ["HP;%"]),
      tit: tit1 === null ? tit2 : tit2 === null ? tit1 : Math.max(tit1, tit2),
      tit1,
      tit2,
      egt: averageValues(egtValues),
      ...Object.fromEntries(egtValues.map((value, cylinder) => [`egt${cylinder + 1}`, value])),
      cht: averageValues(chtValues),
      ...Object.fromEntries(chtValues.map((value, cylinder) => [`cht${cylinder + 1}`, value])),
      fuelFlow: valueFrom(row, ["FLOW;GPH"]),
      fuelUsed: valueFrom(row, ["FLIGHT;GAL"]),
      fuelPressure: valueFrom(row, ["FUEL P;PSI"]),
      buss: valueFrom(row, ["VOLTS;V"]),
      amps: valueFrom(row, ["AMPS;A"]),
      oat: valueFrom(row, ["OAT;*F"]),
      carbTemp: valueFrom(row, ["CARB T;*F"]),
      oilTemp: valueFrom(row, ["OIL T;*F"]),
      oilPressure: valueFrom(row, ["OIL P;PSI"]),
      rpm,
      map: valueFrom(row, ["MP;InHg", "M.P.;HG"]),
      ias: null,
      tas: null,
      groundSpeed: valueFrom(row, ["Gnd Speed"]),
      altitude: gpsAltitude,
      pressureAltitude: valueFrom(row, ["P-ALT;F", "PRESS ALT;F", "PRESSURE ALT;F"]),
      baroPressure: valueFrom(row, ["BARO;InHg", "BARO;INHG", "ALT SET;InHg", "ALTIMETER;InHg"]),
      verticalSpeed: null,
      latitude: valueFrom(row, ["Latitude"], parseLatitude),
      longitude: valueFrom(row, ["Longitude"], parseLongitude),
      gpsAltitude,
      gpsSource: "ei-cgr",
    };
  });
  // The 30P repeats its last GPS position while moving, refreshes latitude
  // and longitude in separate seconds, and its feed can drop or flip
  // characters; see the helpers for what each step removes.
  return suppressImplausibleGpsJumps(collapseGpsRefreshBursts(suppressHeldGpsPositions(points)));
}

export function parseEiCgr30cPointsFromModule(text) {
  const { rows, prefaceLines } = rowsFromHeader(text, (_line, values) => values[0] === "TIME" && values[1] === "RPMLEFT;RPM", 1);
  const validRows = validEiCgrClockRows(rows);
  const sampleRate = eiCgrSampleRateFromModule(validRows);
  const elapsed = eiCgrElapsedSecondsFromModule(validRows, sampleRate);
  const localDate = eiCgrLocalDateFromModule(prefaceLines);
  const timestamps = localTimestampsFromClockRows(validRows, "TIME", localDate);
  return validRows.map((row, index) => {
    const displayTime = String(row.TIME ?? "").trim();
    const tit1 = valueFrom(row, ["TIT1;*F", "TIT 1;*F", "TIT;*F"]) ?? cToF(valueFrom(row, ["TIT1;*C", "TIT 1;*C", "TIT;*C"]));
    const tit2 = valueFrom(row, ["TIT2;*F", "TIT 2;*F"]) ?? cToF(valueFrom(row, ["TIT2;*C", "TIT 2;*C"]));
    return {
      elapsedSeconds: elapsed[index] ?? index / sampleRate,
      timestamp: timestamps[index],
      displayTime,
      phase: null,
      percentPower: valueFrom(row, ["HP;%"]),
      tit: tit1 === null ? tit2 : tit2 === null ? tit1 : Math.max(tit1, tit2),
      tit1,
      tit2,
      egt: null,
      cht: null,
      fuelFlow: null,
      fuelUsed: null,
      buss: valueFrom(row, ["VOLTS;V"]),
      oat: valueFrom(row, ["OAT;*F"]),
      carbTemp: valueFrom(row, ["CARB T;*F"]) ?? cToF(valueFrom(row, ["CARB T;*C"])),
      oilTemp: null,
      oilPressure: null,
      rpm: averageValues([
        valueFrom(row, ["RPMLEFT;RPM"]),
        valueFrom(row, ["RPMRIGHT;RPM"]),
        valueFrom(row, ["RPM;***"]),
      ]),
      map: null,
      ias: null,
      tas: null,
      groundSpeed: null,
      altitude: null,
      verticalSpeed: null,
      fuelPressure: valueFrom(row, ["FUEL P;PSI"]),
      fuelLeft: valueFrom(row, ["FUEL L;GAL", "L FUEL;GAL"]),
      fuelRight: valueFrom(row, ["FUEL R;GAL", "R FUEL;GAL"]),
      amps: valueFrom(row, ["AMPS;A"]),
      alternatorAmps: valueFrom(row, ["ALT;A"]),
      batteryAmps: valueFrom(row, ["BATT;A"]),
    };
  });
}

export function parseEiCgr30cLegacyPointsFromModule(text) {
  const { rows, prefaceLines } = rowsFromHeader(text, (_line, values) => values[0] === "TIME" && values[1] === "RPMLEFT;RPM", 1);
  const validRows = validEiCgrClockRows(rows);
  const sampleRate = eiCgrSampleRateFromModule(validRows);
  const elapsed = eiCgrElapsedSecondsFromModule(validRows, sampleRate);
  const localDate = eiCgrLocalDateFromModule(prefaceLines);
  const timestamps = localTimestampsFromClockRows(validRows, "TIME", localDate);
  return validRows.map((row, index) => {
    const displayTime = String(row.TIME ?? "").trim();
    const egt = valueFrom(row, ["EGT;*F"]);
    const cht = valueFrom(row, ["CHT;*F"]);
    const tit1 = valueFrom(row, ["TIT1;*F", "TIT 1;*F", "TIT;*F"]) ?? cToF(valueFrom(row, ["TIT1;*C", "TIT 1;*C", "TIT;*C"]));
    const tit2 = valueFrom(row, ["TIT2;*F", "TIT 2;*F"]) ?? cToF(valueFrom(row, ["TIT2;*C", "TIT 2;*C"]));
    return {
      elapsedSeconds: elapsed[index] ?? index / sampleRate,
      timestamp: timestamps[index],
      displayTime,
      phase: null,
      percentPower: valueFrom(row, ["HP;%"]),
      tit: tit1 === null ? tit2 : tit2 === null ? tit1 : Math.max(tit1, tit2),
      tit1,
      tit2,
      egt,
      egt1: egt,
      cht,
      cht1: cht,
      fuelFlow: valueFrom(row, ["FLOW;GPH"]),
      fuelUsed: null,
      buss: valueFrom(row, ["VOLTS;V"]),
      oat: null,
      carbTemp: null,
      oilTemp: valueFrom(row, ["OIL T;*F"]),
      oilPressure: valueFrom(row, ["OIL P;PSI"]),
      rpm: averageValues([
        valueFrom(row, ["RPMLEFT;RPM"]),
        valueFrom(row, ["RPMRIGHT;RPM"]),
        valueFrom(row, ["RPM;***"]),
      ]),
      map: valueFrom(row, ["MP;InHg"]),
      ias: null,
      tas: null,
      groundSpeed: null,
      altitude: null,
      verticalSpeed: null,
      fuelPressure: valueFrom(row, ["FUEL P;PSI"]),
      fuelLeft: valueFrom(row, ["L MAIN;GAL", "FUEL L;GAL"]),
      fuelRight: valueFrom(row, ["R MAIN;GAL", "FUEL R;GAL"]),
      amps: valueFrom(row, ["AMPS;A"]),
    };
  });
}

function finiteRpm(point) {
  return Number.isFinite(point?.rpm) ? point.rpm : null;
}

function sortedEiCgrRpmPoints(points) {
  return points
    .filter((point) => Number.isFinite(point?.elapsedSeconds) && Number.isFinite(finiteRpm(point)))
    .sort((a, b) => a.elapsedSeconds - b.elapsedSeconds);
}

function hasContinuousSamples(points) {
  return points.every((point, index) => (
    index === 0
    || Math.abs((point.elapsedSeconds ?? 0) - (points[index - 1].elapsedSeconds ?? 0)) <= CGR_RPM_SYNC_MAX_SAMPLE_GAP_SECONDS
  ));
}

function recentContinuousWindow(points, fromIndex, anchorSeconds, maxSpanSeconds) {
  const window = [];
  for (let index = fromIndex; index >= 0; index -= 1) {
    const point = points[index];
    if (!point) break;
    const pointSeconds = point.elapsedSeconds ?? 0;
    // `points` is sorted ascending by elapsedSeconds, so scanning backward the
    // gap to the anchor only grows. Once it passes the edge-search span the
    // caller discards this point — and every earlier one — anyway, so stop.
    // This bounds the scan to the span window instead of walking to index 0,
    // keeping findEiCgrRpmEdges near-linear rather than O(n^2) on long logs.
    if (anchorSeconds - pointSeconds > maxSpanSeconds) break;
    const next = window[0];
    if (next && Math.abs((next.elapsedSeconds ?? 0) - pointSeconds) > CGR_RPM_SYNC_MAX_SAMPLE_GAP_SECONDS) break;
    window.unshift(point);
  }
  return window;
}

function hasSustainedRpm(points, predicate) {
  let runLength = 0;
  for (const point of points) {
    runLength = predicate(finiteRpm(point) ?? 0) ? runLength + 1 : 0;
    if (runLength >= CGR_RPM_SYNC_MIN_SUSTAINED_SAMPLES) return true;
  }
  return false;
}

function findEiCgrRpmEdges(points) {
  const rpmPoints = sortedEiCgrRpmPoints(points);
  if (rpmPoints.length < CGR_RPM_SYNC_MIN_SUSTAINED_SAMPLES + 1) return [];

  const edges = [];
  for (let index = 0; index <= rpmPoints.length - CGR_RPM_SYNC_MIN_SUSTAINED_SAMPLES; index += 1) {
    const runningWindow = rpmPoints.slice(index, index + CGR_RPM_SYNC_MIN_SUSTAINED_SAMPLES);
    if (
      hasContinuousSamples(runningWindow)
      && runningWindow.every((point) => (finiteRpm(point) ?? 0) >= CGR_RPM_SYNC_STARTUP_THRESHOLD)
    ) {
      const lookback = recentContinuousWindow(rpmPoints, index - 1, rpmPoints[index].elapsedSeconds ?? 0, CGR_RPM_SYNC_EDGE_SEARCH_SECONDS);
      if (lookback.some((point) => (finiteRpm(point) ?? 0) <= CGR_RPM_SYNC_ZERO_THRESHOLD)) {
        edges.push({ kind: "startup", elapsedSeconds: rpmPoints[index].elapsedSeconds });
      }
    }

    const zeroWindow = rpmPoints.slice(index, index + CGR_RPM_SYNC_MIN_SUSTAINED_SAMPLES);
    if (
      hasContinuousSamples(zeroWindow)
      && zeroWindow.every((point) => (finiteRpm(point) ?? 0) <= CGR_RPM_SYNC_ZERO_THRESHOLD)
    ) {
      const lookback = recentContinuousWindow(rpmPoints, index - 1, rpmPoints[index].elapsedSeconds ?? 0, CGR_RPM_SYNC_EDGE_SEARCH_SECONDS);
      if (hasSustainedRpm(lookback, (rpm) => rpm >= CGR_RPM_SYNC_STARTUP_THRESHOLD)) {
        edges.push({ kind: "shutdown", elapsedSeconds: rpmPoints[index].elapsedSeconds });
      }
    }
  }

  return edges;
}

function median(values) {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) return null;
  return sorted[Math.floor((sorted.length - 1) / 2)];
}

export function estimateEiCgr30cAuxRpmOffsetDetailedFromModule(primaryPoints, auxPoints) {
  const primaryEdges = findEiCgrRpmEdges(primaryPoints);
  const auxEdges = findEiCgrRpmEdges(auxPoints);
  const candidates = [];
  primaryEdges.forEach((primaryEdge) => {
    auxEdges.forEach((auxEdge) => {
      if (primaryEdge.kind !== auxEdge.kind) return;
      const offset = primaryEdge.elapsedSeconds - auxEdge.elapsedSeconds;
      if (Number.isFinite(offset) && Math.abs(offset) <= CGR_RPM_SYNC_MAX_OFFSET_SECONDS) {
        candidates.push({ offset, kind: primaryEdge.kind });
      }
    });
  });
  if (!candidates.length) return { offsetSeconds: 0, matched: false };

  const groups = [];
  candidates
    .sort((a, b) => a.offset - b.offset)
    .forEach((candidate) => {
      const group = groups.find((entry) => Math.abs(candidate.offset - entry.offset) <= CGR_RPM_SYNC_OFFSET_GROUP_TOLERANCE_SECONDS);
      if (group) {
        group.candidates.push(candidate);
        group.offset = median(group.candidates.map((entry) => entry.offset)) ?? group.offset;
        return;
      }
      groups.push({ offset: candidate.offset, candidates: [candidate] });
    });

  const best = groups.sort((a, b) => {
    const aKinds = new Set(a.candidates.map((candidate) => candidate.kind)).size;
    const bKinds = new Set(b.candidates.map((candidate) => candidate.kind)).size;
    return bKinds - aKinds
      || b.candidates.length - a.candidates.length
      || Math.abs(a.offset) - Math.abs(b.offset);
  })[0];
  return Number.isFinite(best?.offset)
    ? { offsetSeconds: best.offset, matched: true }
    : { offsetSeconds: 0, matched: false };
}

export function estimateEiCgr30cAuxRpmOffsetSecondsFromModule(primaryPoints, auxPoints) {
  return estimateEiCgr30cAuxRpmOffsetDetailedFromModule(primaryPoints, auxPoints).offsetSeconds;
}

// Offset semantics match the edge estimator: offsetSeconds = primary − aux, the
// shift ADDED to 30C elapsed times to land them on the 30P timeline.
export function estimateEiCgr30cAuxRpmCorrelationOffsetDetailedFromModule(primaryPoints, auxPoints) {
  const noMatch = { offsetSeconds: 0, matched: false };
  const primaryBySecond = new Map();
  for (const point of primaryPoints) {
    if (Number.isFinite(point?.elapsedSeconds) && Number.isFinite(point?.rpm)) {
      primaryBySecond.set(Math.round(point.elapsedSeconds), point.rpm);
    }
  }
  const auxSamples = [];
  for (const point of auxPoints) {
    if (Number.isFinite(point?.elapsedSeconds) && Number.isFinite(point?.rpm)) {
      auxSamples.push([Math.round(point.elapsedSeconds), point.rpm]);
    }
  }
  if (primaryBySecond.size < CGR_RPM_CORRELATION_MIN_OVERLAP_SAMPLES
    || auxSamples.length < CGR_RPM_CORRELATION_MIN_OVERLAP_SAMPLES) {
    return noMatch;
  }

  const minVariance = CGR_RPM_CORRELATION_MIN_RPM_STDDEV ** 2;
  const scores = [];
  for (let lag = -CGR_RPM_SYNC_MAX_OFFSET_SECONDS; lag <= CGR_RPM_SYNC_MAX_OFFSET_SECONDS; lag += 1) {
    let n = 0;
    let sumPrimary = 0;
    let sumAux = 0;
    let sumPrimarySq = 0;
    let sumAuxSq = 0;
    let sumCross = 0;
    for (const [second, auxRpm] of auxSamples) {
      const primaryRpm = primaryBySecond.get(second + lag);
      if (primaryRpm === undefined) continue;
      n += 1;
      sumPrimary += primaryRpm;
      sumAux += auxRpm;
      sumPrimarySq += primaryRpm * primaryRpm;
      sumAuxSq += auxRpm * auxRpm;
      sumCross += primaryRpm * auxRpm;
    }
    if (n < CGR_RPM_CORRELATION_MIN_OVERLAP_SAMPLES) continue;
    const primaryVariance = sumPrimarySq / n - (sumPrimary / n) ** 2;
    const auxVariance = sumAuxSq / n - (sumAux / n) ** 2;
    if (primaryVariance < minVariance || auxVariance < minVariance) continue;
    const r = (sumCross / n - (sumPrimary / n) * (sumAux / n)) / Math.sqrt(primaryVariance * auxVariance);
    if (Number.isFinite(r)) scores.push({ lag, r });
  }
  if (!scores.length) return noMatch;

  const best = scores.reduce((left, right) => (right.r > left.r ? right : left));
  if (best.r < CGR_RPM_CORRELATION_MIN_R) return noMatch;
  // Every lag competitive with the peak must sit in its immediate neighborhood
  // (a broad unimodal peak is fine; two distant competitive peaks are not).
  const hasDistantCompetitor = scores.some((score) =>
    score.r >= best.r - CGR_RPM_CORRELATION_PEAK_MARGIN
    && Math.abs(score.lag - best.lag) > CGR_RPM_CORRELATION_PEAK_NEIGHBORHOOD_SECONDS);
  if (hasDistantCompetitor) return noMatch;
  return { offsetSeconds: best.lag, matched: true };
}

const EI_CGR_30C_AUX_FIELDS = ["buss", "oat", "rpm", "fuelPressure", "fuelLeft", "fuelRight", "amps", "alternatorAmps", "batteryAmps", "carbTemp", "percentPower", "tit", "tit1", "tit2"];

export function mergeEiCgr30cAuxDetailedFromModule(primaryPoints, auxPoints, options: Record<string, any> = {}) {
  const fillRpm = options.fillRpm !== false;
  const onlyMissingChannels = options.onlyMissingChannels === true;
  const auxRpmOffsetSeconds = Number.isFinite(options.offsetSeconds)
    ? options.offsetSeconds
    : estimateEiCgr30cAuxRpmOffsetSecondsFromModule(primaryPoints, auxPoints);
  const primaryHasChannel = new Map(EI_CGR_30C_AUX_FIELDS.map((key) => [
    key,
    primaryPoints.some((point) => Number.isFinite(point?.[key])),
  ]));
  const fillableFields = EI_CGR_30C_AUX_FIELDS.filter((key) => (
    (fillRpm || key !== "rpm")
    && (!onlyMissingChannels || !primaryHasChannel.get(key))
  ));
  const sortedAux = auxPoints
    .map((point) => ({
      ...point,
      elapsedSeconds: Number.isFinite(point?.elapsedSeconds) ? point.elapsedSeconds + auxRpmOffsetSeconds : point?.elapsedSeconds,
    }))
    .sort((a, b) => (a.elapsedSeconds ?? 0) - (b.elapsedSeconds ?? 0));
  const auxFilledKeys = new Set<string>();
  let auxIndex = 0;
  const points = primaryPoints.map((point) => {
    while (
      auxIndex < sortedAux.length - 1
      && Math.abs((sortedAux[auxIndex + 1].elapsedSeconds ?? 0) - point.elapsedSeconds) <= Math.abs((sortedAux[auxIndex].elapsedSeconds ?? 0) - point.elapsedSeconds)
    ) {
      auxIndex += 1;
    }
    const aux = sortedAux[auxIndex];
    const merged = { ...point };
    if (aux && Math.abs((aux.elapsedSeconds ?? 0) - point.elapsedSeconds) <= 1.25) {
      let filled = false;
      fillableFields.forEach((key) => {
        if (!Number.isFinite(merged[key]) && Number.isFinite(aux[key])) {
          merged[key] = aux[key];
          auxFilledKeys.add(key);
          filled = true;
        }
      });
      merged.rawSampleCount = (point.rawSampleCount ?? 1) + (aux.rawSampleCount ?? 1);
      if (filled && Array.isArray(aux.sourceSampleIndices)) {
        merged.sourceSampleIndices = [...new Set([...(point.sourceSampleIndices ?? []), ...aux.sourceSampleIndices])];
      }
    }
    return merged;
  });
  const auxSeriesKeys = [...auxFilledKeys].filter((key) => !primaryHasChannel.get(key)).sort();
  return { points, auxSeriesKeys };
}

export function mergeEiCgr30cAuxFromModule(primaryPoints, auxPoints) {
  return mergeEiCgr30cAuxDetailedFromModule(primaryPoints, auxPoints).points;
}

export function parseEiCgrCsvWithDeps(text, hooks: Record<string, any> = {}) {
  const finalizeParsedPoints = requireEiCgrParserHook(hooks, "finalizeParsedPoints");
  const resamplePointsToOneHz = requireEiCgrParserHook(hooks, "resamplePointsToOneHz");
  return finalizeParsedPoints(resamplePointsToOneHz(parseEiCgr30pPointsFromModule(text)));
}

export function parseEiCgr30cCsvWithDeps(text, hooks: Record<string, any> = {}) {
  const finalizeParsedPoints = requireEiCgrParserHook(hooks, "finalizeParsedPoints");
  const resamplePointsToOneHz = requireEiCgrParserHook(hooks, "resamplePointsToOneHz");
  return finalizeParsedPoints(resamplePointsToOneHz(parseEiCgr30cPointsFromModule(text), ["fuelLeft", "fuelRight"]));
}

export function parseEiCgr30cLegacyCsvWithDeps(text, hooks: Record<string, any> = {}) {
  const finalizeParsedPoints = requireEiCgrParserHook(hooks, "finalizeParsedPoints");
  const resamplePointsToOneHz = requireEiCgrParserHook(hooks, "resamplePointsToOneHz");
  return finalizeParsedPoints(resamplePointsToOneHz(parseEiCgr30cLegacyPointsFromModule(text)));
}

function firstFiniteElapsedSeconds(points) {
  for (const point of points) {
    if (Number.isFinite(point?.elapsedSeconds)) return point.elapsedSeconds;
  }
  return null;
}

export function parseEiCgrComboCsvDetailedWithDeps(primaryText, auxText, hooks: Record<string, any> = {}, options: Record<string, any> = {}) {
  const capture = createSourceCapture(hooks);
  hooks = capture.hooks;
  const finalizeParsedPoints = requireEiCgrParserHook(hooks, "finalizeParsedPoints");
  const resamplePointsToOneHz = requireEiCgrParserHook(hooks, "resamplePointsToOneHz");
  const pairingMode = options.pairingMode === "loose" ? "loose" : "overlap";
  const primaryPoints = resamplePointsToOneHz(parseEiCgr30pPointsFromModule(primaryText));
  const auxPoints = resamplePointsToOneHz(parseEiCgr30cPointsFromModule(auxText), ["fuelLeft", "fuelRight"]);
  const edgeEstimate = estimateEiCgr30cAuxRpmOffsetDetailedFromModule(primaryPoints, auxPoints);
  // No usable edges (e.g. the 30C powered on mid-flight and lost power before
  // engine stop): fall back to cross-correlating the RPM traces both units
  // recorded over their clock overlap.
  const correlationEstimate = edgeEstimate.matched
    ? null
    : estimateEiCgr30cAuxRpmCorrelationOffsetDetailedFromModule(primaryPoints, auxPoints);
  const rpmSynced = edgeEstimate.matched || Boolean(correlationEstimate?.matched);
  // Loose pairs without a trustworthy RPM sync keep both units' data but stay
  // time-unaligned: the 30C fills only channels the 30P lacks entirely (never RPM), so
  // misaligned samples can't blend into a primary-sourced channel.
  const unaligned = !rpmSynced && pairingMode === "loose";
  const comboAlignment = edgeEstimate.matched
    ? "rpm-edge"
    : correlationEstimate?.matched
      ? "rpm-correlation"
      : unaligned ? "unaligned" : "clock";
  let offsetSeconds = edgeEstimate.matched
    ? edgeEstimate.offsetSeconds
    : correlationEstimate?.matched
      ? correlationEstimate.offsetSeconds
      : 0;
  if (unaligned) {
    // A loose pair exists precisely because the two clocks don't overlap well, so
    // an offset-0 merge would drop most (often all) 30C samples outside the 30P
    // window. With no trustworthy RPM sync, overlay the 30C by aligning recording
    // starts — a display heuristic only; the exclusivity rule keeps the two units'
    // traces from ever being read against each other.
    const primaryStart = firstFiniteElapsedSeconds(primaryPoints);
    const auxStart = firstFiniteElapsedSeconds(auxPoints);
    offsetSeconds = Number.isFinite(primaryStart) && Number.isFinite(auxStart)
      ? primaryStart - auxStart
      : 0;
  }
  const merged = mergeEiCgr30cAuxDetailedFromModule(primaryPoints, auxPoints, {
    offsetSeconds,
    fillRpm: !unaligned,
    onlyMissingChannels: unaligned,
  });
  const points = finalizeParsedPoints(merged.points);
  const sparsePosition = gpsPositionSparsityWarning(points);
  return {
    points,
    comboAlignment,
    comboOffsetSeconds: offsetSeconds,
    sourceData: capture.finish([
      csvSourceDocument(primaryText, "ei-cgr", { role: "primary" }),
      csvSourceDocument(auxText, "ei-cgr-30c", { role: "auxiliary" }),
    ]),
    auxSeriesKeys: merged.auxSeriesKeys,
    parserWarnings: [
      ...(unaligned
        ? [createParserWarning({
          code: "cgr-30c-unaligned",
          message: "The CGR-30C clock could not be aligned with the CGR-30P, so 30C channels are display-only.",
          severity: "info",
        })]
        : []),
      ...(sparsePosition ? [sparsePosition] : []),
    ],
  };
}

export function parseEiCgrComboCsvWithDeps(primaryText, auxText, hooks: Record<string, any> = {}) {
  return parseEiCgrComboCsvDetailedWithDeps(primaryText, auxText, hooks).points;
}
